import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Rating,
  Stack,
  TextField,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export default function ReviewsPage() {
  const [form, setForm] = useState({ name: '', contact: '', content: '', rating: 0, images: [] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  const canSubmit = useMemo(() => form.name.trim() !== '' && form.rating > 0, [form]);

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const res = await axios.get(`${API_BASE}/api/reviews`);
      // Laravel pagination format expected; fallback to array
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      setReviews(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    // Limit number of images to 6 to keep payload small
    setForm((f) => ({ ...f, images: files.slice(0, 6) }));
  };

  // Basic image compression using canvas (no external deps)
  const compressImage = (file, { maxDimension = 1600, quality = 0.8 } = {}) =>
    new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          const scale = Math.min(1, maxDimension / Math.max(width, height));
          const targetW = Math.round(width * scale);
          const targetH = Math.round(height * scale);

          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetW, targetH);

          canvas.toBlob(
            (blob) => {
              if (!blob) return resolve(file);
              // Keep original filename, change type to JPEG for better compression
              const name = file.name.replace(/\.(png|webp|gif|bmp|heic|heif)$/i, '.jpg');
              const compressed = new File([blob], name, { type: 'image/jpeg' });
              resolve(compressed);
            },
            'image/jpeg',
            quality,
          );
        };
        img.onerror = () => resolve(file);
        img.src = URL.createObjectURL(file);
      } catch {
        // Fallback to original file on any error
        resolve(file);
      }
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      if (form.contact) fd.append('contact', form.contact);
      if (form.content) fd.append('content', form.content);
      fd.append('rating', String(form.rating));
      // Compress images sequentially to control memory usage
      for (const file of form.images || []) {
        // Skip tiny files under ~150KB
        const toProcess = file.size > 150 * 1024 ? await compressImage(file) : file;
        fd.append('images[]', toProcess);
      }

      await axios.post(`${API_BASE}/api/reviews`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess('Review submitted. Thank you!');
      setForm({ name: '', contact: '', content: '', rating: 0, images: [] });
      await fetchReviews();
    } catch (e) {
      let msg = e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : (e.message || 'Failed to submit');
      if (e?.response?.status === 413) {
        msg = 'Upload too large. Please upload fewer/smaller images. We compress automatically, but your server rejected the request.';
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" fontWeight={800} gutterBottom>
        Reviews
      </Typography>

      <Box component="form" onSubmit={submit} sx={{ mb: 6 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name *"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact"
              value={form.contact}
              onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Your review"
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ minWidth: 100 }}>Rating *</Typography>
              <Rating
                name="rating"
                value={form.rating}
                onChange={(_, value) => setForm((f) => ({ ...f, rating: value || 0 }))}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button component="label" variant="outlined">
              Upload images
              <input hidden type="file" accept="image/*" multiple onChange={onFileChange} />
            </Button>
            {form.images?.length > 0 && (
              <Chip sx={{ ml: 2 }} label={`${form.images.length} selected`} />
            )}
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button type="submit" variant="contained" disabled={!canSubmit || submitting}>
              {submitting ? 'Submitting…' : 'Submit Review'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Typography variant="h5" fontWeight={800} gutterBottom>
        Recent Reviews
      </Typography>
      {loadingReviews ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {reviews.map((r) => (
            <Grid key={r.id} item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography fontWeight={700}>{r.name}</Typography>
                    <Rating value={Number(r.rating || 0)} readOnly size="small" />
                  </Stack>
                  {r.content && (
                    <Typography variant="body2" color="text.secondary" mb={1}>{r.content}</Typography>
                  )}
                  {Array.isArray(r.images) && r.images.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {r.images.slice(0, 4).map((img, idx) => (
                        <CardMedia
                          key={idx}
                          component="img"
                          sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee' }}
                          image={`${API_BASE}/storage/${img}`}
                          alt="review"
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
